import { useState, useEffect, useRef } from "react";
import { Sheet } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAssistant, type AssistantMessage } from "@/hooks/useAssistant";
import { useAuth } from "@/hooks/useAuth";
import { MessageCircle, Sparkles, Send, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import Markdown from "react-markdown";

// ─── Message Bubble ──────────────────────────────────────────────────────────

function MessageBubble({ message }: { message: AssistantMessage }) {
  const isUser = message.role === "user";

  return (
    <div className={cn("flex", isUser ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "max-w-[85%] rounded-lg px-3 py-2 text-sm",
          isUser
            ? "bg-teal text-white ml-8"
            : "bg-sidebar text-text mr-8"
        )}
      >
        {!isUser && (
          <div className="mb-1 flex items-center gap-1">
            <Sparkles className="h-3 w-3 text-teal" />
            <span className="text-xs font-semibold text-teal">Assistant</span>
          </div>
        )}
        {isUser ? (
          <div className="whitespace-pre-wrap break-words font-body leading-relaxed">
            {message.content}
          </div>
        ) : (
          <div className="break-words font-body leading-relaxed prose-assistant">
            <Markdown
              components={{
                h1: ({ children }) => <h3 className="mt-2 mb-1 text-sm font-bold text-text">{children}</h3>,
                h2: ({ children }) => <h3 className="mt-2 mb-1 text-sm font-bold text-text">{children}</h3>,
                h3: ({ children }) => <h4 className="mt-2 mb-1 text-sm font-semibold text-text">{children}</h4>,
                p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                ul: ({ children }) => <ul className="mb-2 ml-4 list-disc space-y-0.5 last:mb-0">{children}</ul>,
                ol: ({ children }) => <ol className="mb-2 ml-4 list-decimal space-y-0.5 last:mb-0">{children}</ol>,
                li: ({ children }) => <li>{children}</li>,
                strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                a: ({ href, children }) => (
                  <a href={href} className="text-teal underline hover:text-teal/80" target="_blank" rel="noopener noreferrer">
                    {children}
                  </a>
                ),
                code: ({ children }) => <code className="rounded bg-white/60 px-1 py-0.5 font-mono text-xs">{children}</code>,
              }}
            >
              {message.content}
            </Markdown>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Typing Indicator ────────────────────────────────────────────────────────

function TypingIndicator() {
  return (
    <div className="flex justify-start">
      <div className="bg-sidebar rounded-lg px-4 py-3 mr-8">
        <div className="flex items-center gap-1">
          <Sparkles className="h-3 w-3 text-teal mr-1" />
          <span
            className="inline-block h-2 w-2 rounded-full bg-text-muted animate-bounce"
            style={{ animationDelay: "0ms" }}
          />
          <span
            className="inline-block h-2 w-2 rounded-full bg-text-muted animate-bounce"
            style={{ animationDelay: "150ms" }}
          />
          <span
            className="inline-block h-2 w-2 rounded-full bg-text-muted animate-bounce"
            style={{ animationDelay: "300ms" }}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Empty State ─────────────────────────────────────────────────────────────

function EmptyState({
  onSuggestionClick,
  isPlatform,
  firstName,
}: {
  onSuggestionClick: (text: string) => void;
  isPlatform: boolean;
  firstName: string | null;
}) {
  const suggestions = isPlatform
    ? [
        "How do I invite a new user?",
        "What are drip sequences?",
        "How do I create a campaign?",
      ]
    : [
        "How do I invite a new user?",
        "What are groups used for?",
        "How do I create an invite link?",
      ];

  return (
    <div className="flex flex-col items-center justify-center h-full text-center px-4 py-12">
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-teal/10">
        <Sparkles className="h-6 w-6 text-teal" />
      </div>
      <h3 className="font-heading text-lg font-semibold text-text mb-1">
        Hey{firstName ? `, ${firstName}` : ""}! I'm your Studio Manager assistant.
      </h3>
      <p className="font-body text-sm text-text-muted mb-6 max-w-[280px]">
        I know my way around this place. Ask me anything — I promise I'm more fun than a help doc.
      </p>
      <div className="flex flex-col gap-2 w-full max-w-[280px]">
        {suggestions.map((s) => (
          <Button
            key={s}
            variant="outline"
            size="sm"
            className="text-left justify-start"
            onClick={() => onSuggestionClick(s)}
          >
            {s}
          </Button>
        ))}
      </div>
    </div>
  );
}

// ─── Assistant Panel ─────────────────────────────────────────────────────────

export function AssistantPanel() {
  const {
    messages,
    isLoading,
    isOpen,
    setIsOpen,
    sendMessage,
    clearConversation,
  } = useAssistant();
  const { role, orgUser } = useAuth();

  const firstName = orgUser?.name?.split(" ")[0] || null;
  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isPlatform =
    role === "PLATFORM_OWNER" || role === "PLATFORM_ADMIN";

  // Auto-scroll on new messages
  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSend = () => {
    if (!input.trim() || isLoading) return;
    sendMessage(input.trim());
    setInput("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* FAB trigger */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-teal text-white shadow-lg hover:bg-teal/85 transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-teal focus:ring-offset-2"
          aria-label="Open assistant"
        >
          <MessageCircle className="h-5 w-5" />
        </button>
      )}

      {/* Sheet panel */}
      <Sheet
        open={isOpen}
        onClose={() => setIsOpen(false)}
        className="flex max-w-[440px] flex-col !p-0 !overflow-hidden"
      >
        {/* Header */}
        <div className="border-b border-border px-4 py-3 pr-12">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-teal" />
              <h2 className="font-heading text-lg font-bold text-text">
                Studio Assistant
              </h2>
            </div>
            {messages.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearConversation}
                aria-label="Clear conversation"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
          <p className="mt-0.5 font-body text-xs text-text-muted">
            Ask me anything about Studio Manager
          </p>
        </div>

        {/* Messages area */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {messages.length === 0 ? (
            <EmptyState
              onSuggestionClick={sendMessage}
              isPlatform={isPlatform}
              firstName={firstName}
            />
          ) : (
            <div className="space-y-3">
              {messages.map((msg) => (
                <MessageBubble key={msg.id} message={msg} />
              ))}
              {isLoading && <TypingIndicator />}
            </div>
          )}
          <div ref={scrollRef} />
        </div>

        {/* Input area */}
        <div className="border-t border-border px-4 py-3">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask a question..."
              disabled={isLoading}
              className="flex-1 rounded-md border border-border bg-white px-3 py-2 font-body text-sm text-text placeholder-text-muted focus:outline-none focus:ring-1 focus:border-teal focus:ring-teal transition-colors disabled:opacity-50"
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
              className="flex h-9 w-9 items-center justify-center rounded-md bg-teal text-white hover:bg-teal/85 transition-colors disabled:opacity-50 disabled:pointer-events-none focus:outline-none focus:ring-2 focus:ring-teal focus:ring-offset-2"
              aria-label="Send message"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      </Sheet>
    </>
  );
}
